import { useState, useEffect } from 'react';

import { useDispatch } from 'react-redux';
import {
  createRootRoute,
  Link,
  Outlet,
  useNavigate,
} from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { getCurrentUser as getUserInfo } from '@/features/auth/api';
import { setUser } from '@/features/auth/auth-slice';
import { LOCAL_STORAGE_KEYS } from '@/constants';

import { Toaster } from '@/components/ui/toaster';
import { OfflineIndicator } from '@/components/feedback/OfflineIndicator';
import { PendingRequestsIndicator } from '@/components/ui/PendingRequestsIndicator';
import { PageExpressiveLoading } from '@/components/feedback/ExpressiveLoadingIndicator';

import { AppDispatch } from '@/store/store';
import { Button } from '@/components/ui/button';
import { FileQuestion } from '@/lib/icons';

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="mx-auto max-w-md text-center space-y-6">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            <FileQuestion className="h-8 w-8 text-muted-foreground" />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-4xl font-bold tracking-tight">404</h1>
          <h2 className="text-xl font-semibold">Page Not Found</h2>
          <p className="text-muted-foreground">
            The page you are looking for does not exist or has been moved.
          </p>
        </div>
        <div className="flex justify-center">
          <Button asChild>
            <Link to="/">Back to Dashboard</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootComponent() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    // Do nothing for account routes.
    const accountRoutes = ['/login', '/forgot-password', '/reset-password'];
    const currentPath = window.location.pathname;

    if (accountRoutes.includes(currentPath)) {
      setIsLoading(false);
      setError(null);
      return;
    }

    // Check authentication status.
    const checkAuth = async () => {
      setIsLoading(true);
      const result = await getUserInfo();
      setIsLoading(false);
      if (!result.succeeded || !result.value) {
        if (result.statusCode === 401 && currentPath !== '/login') {
          navigate({
            to: '/login',
          });
        } else {
          setError(null);
        }
        return;
      }
      dispatch(setUser(result.value));
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.USER,
        JSON.stringify(result.value)
      );
    };

    checkAuth();
  }, [dispatch, navigate]);

  if (isLoading) {
    return <PageExpressiveLoading text={t('common.loadingWorkspace')} />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <>
      <OfflineIndicator />
      <Outlet />
      <PendingRequestsIndicator />
      <Toaster />
      {/* {import.meta.env.DEV && <TanStackRouterDevtools />} */}
    </>
  );
}
